import { Box, chakra, HStack, Icon, Input, Text } from "@chakra-ui/react";
import { useEffect, useId, useRef, useState } from "react";
import {
  PiArrowsInLineVertical,
  PiArrowsOutLineVertical,
  PiBezierCurve,
  PiBoundingBox,
  PiBracketsCurly,
  PiCards,
  PiCopy,
  PiCopySimple,
  PiEraser,
  PiFastForward,
  PiFileSvg,
  PiGridFour,
  PiLineSegment,
  PiLineSegments,
  PiLineVerticalThin,
  PiShapes,
  PiSpinnerGap,
  PiSquare,
  PiSquareFill,
  PiStack,
  PiTextAlignLeft,
  PiTextT,
  PiTrash,
} from "react-icons/pi";
import { TbBorderRadius } from "react-icons/tb";
import { useNodeHasChildren } from "../hooks/useNodeHasChildren";
import { getNodeBorderWidth } from "../nodes/nodeBorder";
import { getChakraColorHex, LIGHT_BLACK } from "../theme/colorUtils";
import { useCustomColors } from "../theme/useCustomColors";
import type { SelectionToolbarConfig } from "../types";
import { AnimationVariantSelect } from "./AnimationVariantSelect";
import { useFlowData, useFlowDiagramContext } from "./FlowDiagramContext";
import { NodeIconPicker } from "./NodeIconPicker";
import {
  RecentToolbarContext,
  useRecentToolbarAction,
} from "./RecentToolbarAction";
import {
  ToolbarCloseButton as CloseButton,
  ToolbarIconButton,
  ToolbarSelect,
  ToolbarTooltip,
} from "./toolbarControls";

export const DEFAULT_PALETTE = [
  "white",
  "blue.500",
  "teal.500",
  "green.500",
  "yellow.500",
  "orange.500",
  "red.500",
  "purple.500",
  "pink.500",
  "gray.500",
  LIGHT_BLACK,
];
export const TEXT_PALETTE = [
  "gray.900",
  LIGHT_BLACK,
  "gray.600",
  "gray.200",
  "white",
];

type Hide = NonNullable<SelectionToolbarConfig["hide"]>[number];

function useToolbarConfig() {
  const { config } = useFlowDiagramContext();
  const cfg = (config.selectionToolbar || {}) as SelectionToolbarConfig;
  const hidden = new Set<Hide>(cfg.hide || []);
  return {
    palette: cfg.palette || DEFAULT_PALETTE,
    show: (k: Hide) => !hidden.has(k),
  };
}

function Divider() {
  const { border } = useCustomColors();
  return <Icon mx={-1} as={PiLineVerticalThin} boxSize={5} color={border} />;
}

export { CloseButton };

export function CommitInput({
  value,
  placeholder,
  ariaLabel,
  width = "140px",
  type = "text",
  min,
  disabled = false,
  onCommit,
}: {
  value: string;
  placeholder: string;
  ariaLabel: string;
  width?: string;
  type?: "text" | "number";
  min?: number;
  disabled?: boolean;
  onCommit: (v: string) => void;
}) {
  const { colorText } = useCustomColors();
  const [v, setV] = useState(value);
  const committedRef = useRef(false);
  useEffect(() => setV(value), [value]);
  const commit = () => {
    if (disabled || committedRef.current) return;
    committedRef.current = true;
    const t = v.trim();
    if (
      type === "number" &&
      t !== "" &&
      (!Number.isFinite(Number(t)) || Number(t) < (min ?? 0))
    ) {
      setV(value);
      return;
    }
    if (t !== value) onCommit(t);
  };
  return (
    <Input
      aria-label={ariaLabel}
      type={type}
      min={min}
      disabled={disabled}
      size="xs"
      fontSize="16px"
      w={width}
      value={v}
      placeholder={placeholder}
      color={colorText}
      borderWidth={0}
      _focusVisible={{ boxShadow: "none", outline: "none" }}
      borderRadius="8px"
      onFocus={() => {
        committedRef.current = false;
      }}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          commit();
          (e.target as HTMLInputElement).blur();
        }
        if (e.key === "Escape") {
          committedRef.current = true;
          setV(value);
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}

export function Swatches({
  colors,
  value,
  label,
  onPick,
  onReset,
  includeTransparent = true,
}: {
  colors: string[];
  value?: string;
  label: string;
  onPick: (c: string) => void;
  onReset?: () => void;
  includeTransparent?: boolean;
}) {
  const { colorInactive, colorText } = useCustomColors();
  const [expanded, setExpanded] = useState(false);
  const paletteId = useId();
  const toggle = useRef<HTMLButtonElement>(null);
  const selected = value ?? colors[0] ?? "transparent";
  const remember = useRecentToolbarAction(
    <Swatches
      colors={colors}
      value={value}
      label={label}
      onPick={onPick}
      onReset={onReset}
      includeTransparent={includeTransparent}
    />,
  );
  return (
    <RecentToolbarContext.Provider value={null}>
      <HStack
        gap={1}
        aria-label={label}
        role="group"
        onClickCapture={remember}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setExpanded(false);
            toggle.current?.focus();
          }
        }}
      >
        <ToolbarTooltip label={`${label}: ${selected}`}>
          <chakra.button
            ref={toggle}
            type="button"
            aria-label={`${label} colors`}
            aria-expanded={expanded}
            aria-controls={paletteId}
            w={4}
            h={4}
            flexShrink={0}
            borderRadius="full"
            cursor="pointer"
            style={{
              backgroundColor: getChakraColorHex(selected),
              ...(selected === "transparent"
                ? {
                    backgroundImage:
                      "conic-gradient(#fff 25%, #a0aec0 0 50%, #fff 0 75%, #a0aec0 0)",
                    backgroundSize: "8px 8px",
                  }
                : {}),
            }}
            outline={`2px solid ${getChakraColorHex(colorText)}`}
            outlineOffset="1px"
            onClick={() => setExpanded((open) => !open)}
          />
        </ToolbarTooltip>
        {expanded && (
          <HStack id={paletteId} gap={1}>
            {[
              ...new Set([
                ...colors,
                ...(includeTransparent ? ["transparent"] : []),
              ]),
            ].map((c) => {
              const active = value === c;
              return (
                <ToolbarTooltip key={c} label={`${label}: ${c}`}>
                  <Box
                    as="button"
                    aria-label={`${label} ${c}`}
                    aria-pressed={active}
                    w={4}
                    h={4}
                    borderRadius="full"
                    cursor="pointer"
                    style={{
                      backgroundColor: getChakraColorHex(c),
                      ...(c === "transparent"
                        ? {
                            backgroundImage:
                              "conic-gradient(#fff 25%, #a0aec0 0 50%, #fff 0 75%, #a0aec0 0)",
                            backgroundSize: "8px 8px",
                          }
                        : {}),
                    }}
                    outline={
                      active
                        ? `2px solid ${getChakraColorHex(colorText)}`
                        : "none"
                    }
                    outlineOffset="1px"
                    _hover={{ transform: "scale(1.15)" }}
                    onClick={() => onPick(c)}
                    onDoubleClick={(event) => {
                      event.stopPropagation();
                      setExpanded(false);
                      toggle.current?.focus();
                    }}
                  />
                </ToolbarTooltip>
              );
            })}
            {onReset && (
              <ToolbarTooltip label={`Reset ${label.toLowerCase()}`}>
                <ToolbarIconButton
                  aria-label={`Reset ${label}`}
                  icon={<Icon as={PiEraser} boxSize={4} />}
                  size="xs"
                  variant="ghost"
                  color={colorInactive}
                  onClick={() => {
                    onReset();
                    setExpanded(false);
                    toggle.current?.focus();
                  }}
                />
              </ToolbarTooltip>
            )}
          </HStack>
        )}
      </HStack>
    </RecentToolbarContext.Provider>
  );
}

/** Bottom-bar contents while an edge is selected */
export function EdgeTools({
  edgeId,
  onClose,
}: {
  edgeId: string;
  onClose: () => void;
}) {
  const { callbacks, themeColor, config } = useFlowDiagramContext();
  const { colorInactive } = useCustomColors();
  const { edgeById } = useFlowData();
  const { palette, show } = useToolbarConfig();
  const data = edgeById.get(edgeId)?.data;
  const edgeTypes = [
    ...new Set([
      "rounded-orthogonal",
      "orthogonal",
      "straight",
      "bezier",
      ...Object.keys(config.customEdgeTypes ?? {}),
      ...(data?.type ? [data.type] : []),
    ]),
  ];
  const style = data?.isEdgeAnimated
    ? "animated"
    : data?.isEdgeDashed
      ? "dashed"
      : "solid";

  const styles: {
    key: "solid" | "dashed" | "animated";
    icon: any;
    label: string;
  }[] = [
    { key: "solid", icon: PiLineSegment, label: "Solid" },
    { key: "dashed", icon: PiLineSegments, label: "Dashed" },
    { key: "animated", icon: PiFastForward, label: "Animated" },
  ];

  return (
    <>
      <CloseButton onClick={onClose} />
      {show("label") && (
        <>
          <Divider />
          <CommitInput
            ariaLabel="Edge label"
            placeholder="Edge label"
            value={data?.label || ""}
            onCommit={(v) => callbacks.onEdgeLabelChange?.(edgeId, v)}
          />
        </>
      )}
      {show("style") && (
        <>
          <Divider />
          {styles.map((s) => (
            <ToolbarTooltip key={s.key} label={s.label}>
              <ToolbarIconButton
                aria-label={`Edge style ${s.key}`}
                aria-pressed={style === s.key}
                icon={<Icon as={s.icon} boxSize={5} />}
                size="sm"
                variant="ghost"
                color={style === s.key ? themeColor : colorInactive}
                onClick={() => callbacks.onEdgeStyleChange?.(edgeId, s.key)}
              />
            </ToolbarTooltip>
          ))}
          {style === "animated" && (
            <AnimationVariantSelect
              label="Edge animation"
              value={data?.edgeAnimationVariant ?? "dashed"}
              options={
                [
                  { value: "dashed", label: "Dash" },
                  { value: "traveling", label: "Trace" },
                ] as const
              }
              disabled={config.readOnly}
              onChange={(variant) =>
                callbacks.onEdgeAnimationVariantChange?.(edgeId, variant)
              }
            />
          )}
          <ToolbarSelect
            aria-label="Edge shape"
            title={`Edge shape: ${data?.type ?? "Default"}`}
            icon={<PiBezierCurve size={20} />}
            style={{ width: 36 }}
            value={data?.type ?? ""}
            disabled={config.readOnly}
            onValueChange={(value) =>
              callbacks.onEdgeTypeChange?.(edgeId, value || undefined)
            }
          >
            <option value="">Default</option>
            {edgeTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </ToolbarSelect>
        </>
      )}
      {show("icon") && (
        <>
          <Divider />
          <NodeIconPicker key={edgeId} edgeId={edgeId} value={data?.icon} />
          <CommitInput
            ariaLabel="Edge icon"
            placeholder="Icon name or SVG URL"
            value={data?.icon ?? ""}
            onCommit={(icon) => callbacks.onEdgeIconChange?.(edgeId, icon)}
          />
        </>
      )}
      {show("color") && (
        <>
          <Divider />
          <Swatches
            label="Edge color"
            colors={palette}
            value={data?.color}
            onPick={(c) => callbacks.onEdgeColorChange?.(edgeId, c)}
            onReset={() => callbacks.onEdgeColorChange?.(edgeId, "")}
          />
        </>
      )}
      {show("delete") && (
        <>
          <Divider />
          <ToolbarTooltip label="Delete edge">
            <ToolbarIconButton
              aria-label="Delete edge"
              icon={<Icon as={PiTrash} boxSize={5} />}
              size="sm"
              variant="ghost"
              color="red.400"
              onClick={() => {
                callbacks.onEdgeDelete?.(edgeId, data);
                onClose();
              }}
            />
          </ToolbarTooltip>
        </>
      )}
    </>
  );
}

export function BorderRadiusControl({
  ids,
  showDivider = true,
}: {
  ids: string[];
  showDivider?: boolean;
}) {
  const { callbacks, config, nodeGridOptions, fillRadiusEditedIds } =
    useFlowDiagramContext();
  const { colorInactive } = useCustomColors();
  const { nodeById } = useFlowData();
  const [custom, setCustom] = useState(false);
  const radii = ids.map((id) =>
    nodeGridOptions?.fill && !fillRadiusEditedIds.has(id)
      ? 0
      : nodeById.get(id)?.data.viewProps?.borderRadius,
  );
  const mixed = !radii.every((radius) => radius === radii[0]);
  const value = mixed ? "mixed" : String(radii[0] ?? "");
  const presets = ["0", "8", "16", "30", "53", "100"];
  const customMode =
    custom || (!mixed && value !== "" && !presets.includes(value));
  const update = (radius: number | undefined) =>
    ids.forEach((id) => callbacks.onNodeBorderRadiusChange?.(id, radius));
  return (
    <>
      {showDivider && <Divider />}
      <Box
        position="relative"
        w="36px"
        h="30px"
        display="grid"
        placeItems="center"
      >
        <ToolbarSelect
          icon={
            <Icon
              as={TbBorderRadius}
              boxSize={5}
              color={colorInactive}
              aria-hidden="true"
            />
          }
          aria-label="Node border radius"
          title={`Border radius: ${mixed ? "Mixed" : radii[0] === undefined ? "Default" : `${radii[0]}px`}`}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            cursor: "pointer",
          }}
          value={customMode ? "custom" : value}
          disabled={config.readOnly}
          onValueChange={(next) => {
            setCustom(next === "custom");
            if (next !== "custom")
              update(next === "" ? undefined : Number(next));
          }}
        >
          <option value="">Default radius</option>
          {mixed && (
            <option value="mixed" disabled>
              Mixed radii
            </option>
          )}
          {presets.map((radius) => (
            <option key={radius} value={radius}>
              {radius}px radius
            </option>
          ))}
          <option value="custom">Custom radius</option>
        </ToolbarSelect>
      </Box>
      {customMode && (
        <CommitInput
          ariaLabel="Custom border radius"
          placeholder={mixed ? "Mixed" : "Auto"}
          type="number"
          min={0}
          width="72px"
          value={mixed ? "" : value}
          onCommit={(next) => update(next === "" ? undefined : Number(next))}
        />
      )}
    </>
  );
}

/** Bottom-bar contents while one or more nodes are selected */
export function NodeTools({
  ids,
  onClose,
  afterLabel,
}: {
  ids: string[];
  onClose: () => void;
  afterLabel?: React.ReactNode;
}) {
  const { callbacks, colorMode, themeColor, config, setNodeViewMode } =
    useFlowDiagramContext();
  const { colorText, colorInactive } = useCustomColors();
  const { nodeById, parentIds } = useFlowData();
  const { palette, show } = useToolbarConfig();
  const single = ids.length === 1;
  const [selectionError, setSelectionError] = useState("");
  const data = single ? nodeById.get(ids[0])?.data : undefined;
  const typeViews = ids.map(
    (id) => nodeById.get(id)?.data.config?.typeView ?? "",
  );
  const mixedTypeView = typeViews.some((view) => view !== typeViews[0]);
  const typeView = mixedTypeView ? "mixed" : (typeViews[0] ?? "");
  const typeViewChoices = (
    [
      { value: "text", label: "Text", view: "text", icon: PiTextT },
      {
        value: "icon-label",
        label: "Icon Label",
        view: "icon-label",
        icon: PiShapes,
      },
      {
        value: "object",
        label: "Object",
        view: "object",
        icon: PiBracketsCurly,
      },
      { value: "grid", label: "Grid", view: "grid", icon: PiGridFour },
      { value: "parent", label: "Parent", view: "parent", icon: PiStack },
      { value: "media", label: "Media", view: "icon-label", icon: PiFileSvg },
      {
        value: "content",
        label: "Content",
        view: "object",
        icon: PiTextAlignLeft,
      },
      {
        value: "prompt",
        label: "Prompt",
        view: "prompt",
        icon: PiTextAlignLeft,
      },
    ] as const
  ).filter((choice) => choice.view !== "prompt" || !!callbacks.onPromptSubmit);
  const customTypeView = ids.some((id) => {
    const node = nodeById.get(id)?.data;
    return (
      node &&
      (parentIds.has(id) ||
        node.config?.parentContainer ||
        Object.prototype.hasOwnProperty.call(
          config.customViews ?? {},
          node.type,
        ))
    );
  });
  const collapsed = data?.config?.collapsed;
  const hasChildren = useNodeHasChildren(ids[0]);
  const isOutlined = (id: string) =>
    (nodeById.get(id)?.data.viewProps?.outline ??
      (parentIds.has(id) ? config.parentNodeStyle?.outline : false)) === true;
  const outlined = ids.every(isOutlined);
  const mixedOutline = !outlined && ids.some(isOutlined);
  const isAnimated = (id: string) =>
    nodeById.get(id)?.data.isNodeAnimated === true;
  const animated = ids.every(isAnimated);
  const mixedAnimation = !animated && ids.some(isAnimated);
  const animationVariants = ids.map(
    (id) => nodeById.get(id)?.data.nodeAnimationVariant ?? "traveling",
  );
  const animationVariant = animationVariants.every(
    (variant) => variant === animationVariants[0],
  )
    ? animationVariants[0]
    : "";
  const each = (fn: (id: string) => void) => ids.forEach(fn);
  const [borderMode, setBorderMode] = useState(false);
  const editingBorder = borderMode && show("color") && show("borderColor");
  const isDashed = (id: string) =>
    (nodeById.get(id)?.data.viewProps?.borderStyle ??
      (parentIds.has(id) ? config.parentNodeStyle?.borderStyle : "solid")) ===
    "dashed";
  const dashed = ids.every(isDashed);
  const mixedDashed = !dashed && ids.some(isDashed);
  const widths = ids.map((id) => {
    const node = nodeById.get(id)?.data;
    const fallback = parentIds.has(id)
      ? getNodeBorderWidth(config.parentNodeStyle?.borderWidth, 6)
      : node?.type === "objectView"
        ? 3
        : 2;
    return getNodeBorderWidth(node?.viewProps?.borderWidth, fallback);
  });
  const borderWidth = widths.every((width) => width === widths[0])
    ? String(widths[0] ?? "")
    : "";
  const updateColor = (color: string) =>
    each((id) => {
      if (editingBorder) callbacks.onNodeBorderColorChange?.(id, color);
      else callbacks.onNodeColorChange?.(id, "bg", color);
    });

  return (
    <>
      <CloseButton onClick={onClose} />
      <Divider />
      {single && show("label") ? (
        <CommitInput
          ariaLabel="Node label"
          placeholder="Node label"
          value={data?.label ?? data?.headerTitle ?? ""}
          onCommit={(v) => callbacks.onNodeLabelChange?.(ids[0], v)}
        />
      ) : (
        <Text fontSize="xs" color={colorText} whiteSpace="nowrap" px={1}>
          {ids.length} selected
        </Text>
      )}
      {afterLabel}
      {show("typeView") && (
        <ToolbarSelect
          aria-label="Type view"
          title={
            customTypeView
              ? "Type view is managed by the custom renderer or container"
              : `Type view: ${typeView || "None (default)"}`
          }
          icon={<PiCards size={20} />}
          style={{ width: 36 }}
          value={typeView}
          disabled={
            config.readOnly || config.editable === false || customTypeView
          }
          onValueChange={(value) => {
            const view = typeViewChoices.find(
              (choice) => choice.value === value,
            )?.view;
            if (!view) return;
            each((id) => {
              callbacks.onNodeTypeViewChange?.(id, view);
              setNodeViewMode(id, "default");
              callbacks.onNodeViewToggle?.(id);
            });
          }}
        >
          {mixedTypeView && (
            <option value="mixed" disabled>
              Mixed
            </option>
          )}
          {typeViewChoices.map((choice) => (
            <option key={choice.value} value={choice.value}>
              <HStack gap="12px">
                <choice.icon
                  size={20}
                  aria-hidden="true"
                  style={{ flexShrink: 0 }}
                />
                <span>{choice.label}</span>
              </HStack>
            </option>
          ))}
        </ToolbarSelect>
      )}
      {show("style") && (
        <BorderRadiusControl key={ids.join("\u0000")} ids={ids} />
      )}
      {show("outline") && (
        <>
          <Divider />
          <ToolbarTooltip
            label={outlined ? "Restore node fill" : "Remove node fill"}
          >
            <ToolbarIconButton
              aria-label="Toggle node outline"
              aria-pressed={mixedOutline ? "mixed" : outlined}
              icon={
                <Icon as={outlined ? PiSquare : PiSquareFill} boxSize={5} />
              }
              size="sm"
              variant="ghost"
              color={outlined ? themeColor : colorInactive}
              onClick={() =>
                each((id) => callbacks.onNodeOutlineChange?.(id, !outlined))
              }
            />
          </ToolbarTooltip>
        </>
      )}
      {show("color") && (
        <>
          <Divider />
          {show("borderColor") && (
            <ToolbarTooltip
              label={editingBorder ? "Edit node colors" : "Edit node border"}
            >
              <ToolbarIconButton
                aria-label="Edit node border"
                aria-pressed={editingBorder}
                icon={<Icon as={PiBoundingBox} boxSize={5} />}
                size="sm"
                variant="ghost"
                color={editingBorder ? themeColor : colorInactive}
                bg={
                  editingBorder
                    ? colorMode === "dark"
                      ? "whiteAlpha.200"
                      : "blackAlpha.100"
                    : undefined
                }
                onClick={() => setBorderMode((current) => !current)}
              />
            </ToolbarTooltip>
          )}
          <Swatches
            label={editingBorder ? "Border color" : "Background"}
            colors={palette}
            value={
              editingBorder
                ? (data?.viewProps?.borderColor ??
                  (single && parentIds.has(ids[0])
                    ? config.parentNodeStyle?.borderColor
                    : undefined))
                : data?.viewProps?.bgColor
            }
            onPick={updateColor}
            onReset={() => updateColor("")}
          />
          {editingBorder && show("style") && (
            <>
              <Divider />
              <ToolbarTooltip label={dashed ? "Solid border" : "Dashed border"}>
                <ToolbarIconButton
                  aria-label="Toggle dashed border"
                  aria-pressed={mixedDashed ? "mixed" : dashed}
                  icon={
                    <Icon
                      as={dashed ? PiLineSegments : PiLineSegment}
                      boxSize={5}
                    />
                  }
                  size="sm"
                  variant="ghost"
                  color={dashed ? themeColor : colorInactive}
                  onClick={() =>
                    each((id) =>
                      callbacks.onNodeBorderStyleChange?.(
                        id,
                        dashed ? "solid" : "dashed",
                      ),
                    )
                  }
                />
              </ToolbarTooltip>
              <CommitInput
                ariaLabel="Border thickness"
                placeholder={single ? "Auto" : "Mixed"}
                width="64px"
                type="number"
                min={0}
                value={borderWidth}
                onCommit={(value) =>
                  each((id) =>
                    callbacks.onNodeBorderWidthChange?.(
                      id,
                      value === "" ? undefined : Number(value),
                    ),
                  )
                }
              />
            </>
          )}
        </>
      )}
      {show("animation") && (
        <>
          <Divider />
          <ToolbarTooltip
            label={animated ? "Stop border animation" : "Animate node border"}
          >
            <ToolbarIconButton
              aria-label="Toggle node animation"
              aria-pressed={mixedAnimation ? "mixed" : animated}
              icon={<Icon as={PiSpinnerGap} boxSize={5} />}
              size="sm"
              variant="ghost"
              color={animated ? themeColor : colorInactive}
              onClick={() =>
                each((id) => callbacks.onNodeAnimationChange?.(id, !animated))
              }
            />
          </ToolbarTooltip>
          {(animated || mixedAnimation) && (
            <AnimationVariantSelect
              label="Node animation"
              value={animationVariant}
              options={
                [
                  { value: "traveling", label: "Trace" },
                  { value: "pulse", label: "Pulse" },
                ] as const
              }
              disabled={config.readOnly}
              onChange={(variant) =>
                each((id) =>
                  callbacks.onNodeAnimationVariantChange?.(id, variant),
                )
              }
            />
          )}
        </>
      )}
      {!editingBorder && show("textColor") && (
        <>
          <Divider />
          <Swatches
            label="Text color"
            colors={TEXT_PALETTE}
            value={data?.viewProps?.textColor}
            onPick={(c) =>
              each((id) => callbacks.onNodeColorChange?.(id, "text", c))
            }
            onReset={() =>
              each((id) => callbacks.onNodeColorChange?.(id, "text", ""))
            }
          />
        </>
      )}
      {single && show("icon") && (
        <>
          <Divider />
          <NodeIconPicker
            key={ids[0]}
            nodeId={ids[0]}
            value={data?.config?.icon}
          />
          <CommitInput
            ariaLabel="Node icon"
            placeholder="(e.g. PiGear)"
            width="120px"
            value={data?.config?.icon || ""}
            onCommit={(v) => callbacks.onNodeIconChange?.(ids[0], v)}
          />
        </>
      )}
      {single && hasChildren && show("collapse") && collapsed !== undefined && (
        <>
          <Divider />
          <ToolbarTooltip label={collapsed ? "Expand" : "Collapse"}>
            <ToolbarIconButton
              aria-label={collapsed ? "Expand node" : "Collapse node"}
              icon={
                <Icon
                  as={
                    collapsed ? PiArrowsOutLineVertical : PiArrowsInLineVertical
                  }
                  boxSize={5}
                />
              }
              size="sm"
              variant="ghost"
              color={colorInactive}
              onClick={() => callbacks.onNodeCollapse?.(ids[0], !collapsed)}
            />
          </ToolbarTooltip>
        </>
      )}
      {show("delete") && (
        <>
          <Divider />
          <ToolbarTooltip
            label={single ? "Delete node" : `Delete ${ids.length} nodes`}
          >
            <ToolbarIconButton
              aria-label="Delete selected nodes"
              icon={<Icon as={PiTrash} boxSize={5} />}
              size="sm"
              variant="ghost"
              color={colorMode === "dark" ? "red.300" : "red.500"}
              onClick={() => {
                each((id) =>
                  callbacks.onNodeDelete?.(
                    id,
                    nodeById.get(id)?.data || { id },
                  ),
                );
                onClose();
              }}
            />
          </ToolbarTooltip>
        </>
      )}
      <ToolbarTooltip label="Copy selection">
        <ToolbarIconButton
          aria-label="Copy selection"
          icon={<PiCopy size={20} />}
          size="sm"
          variant="ghost"
          color={colorInactive}
          onClick={async () => {
            try {
              await callbacks.onNodesCopy?.(ids);
              setSelectionError("");
            } catch (error) {
              setSelectionError(String(error));
            }
          }}
        />
      </ToolbarTooltip>
      <ToolbarTooltip label="Duplicate selection">
        <ToolbarIconButton
          aria-label="Duplicate selection"
          icon={<PiCopySimple size={20} />}
          size="sm"
          variant="ghost"
          color={colorInactive}
          disabled={config.readOnly || config.editable === false}
          onClick={() => {
            try {
              callbacks.onNodesDuplicate?.(ids);
              setSelectionError("");
            } catch (error) {
              setSelectionError(String(error));
            }
          }}
        />
      </ToolbarTooltip>
      {selectionError && (
        <Text role="alert" fontSize="xs">
          {selectionError}
        </Text>
      )}
    </>
  );
}
